// @flow

import _ from 'lodash';

import { currency } from 'client/utils/format';
import CORE_PRODUCT_TYPES from 'client/constants/productTypes';
import ROUTES from 'client/constants/routes';

import type { IfpAppType, AppType } from 'web-core/types/ifp-app';
import { getEdeIntroRoute } from 'routes/marketplace-ede/utils/routes';

const HEALTH_CARE_EXCHANGE_NAME = 'healthcare.gov';

type CartStateType = {
  cart: {
    ifpApps: Array<IfpAppType>,
  },
  contentfulData: Object,
  ifpApps: Array<IfpAppType>,
};

type CartPropsType = {
  activeAppTypes: Array<AppType>,
  cartApps: Array<IfpAppType>,
  cartAppTypes: Array<AppType>,
  checkoutRoute: string,
  dentalApp: ?IfpAppType,
  hasFFMApp: boolean,
  healthApp: ?IfpAppType,
  isHealthCareGov: boolean,
  premiumTotal: number,
  visionApp: ?IfpAppType,
};

export const getAppByType = (apps: Array<IfpAppType>, type: AppType) => _.find(apps, { type });

export const mapAppsByType = (apps: Array<IfpAppType>): Array<AppType> => _.map(apps, a => a.type);

export const getPremiumTotal = (apps: Array<IfpAppType>, excludeTypes: Array<AppType> | null = null) => {
  const filteredApps = excludeTypes ? _.filter(apps, app => _.indexOf(excludeTypes, app.type) < 0) : apps;
  const premiumTotal = _.reduce(
    filteredApps,
    (memo, app) => {
      const premium = parseFloat(app.premium);
      return memo + premium;
    },
    0,
  );
  return currency(premiumTotal, 2);
};

export const getCheckoutRoute = (
  cartApps: Array<IfpAppType>,
  contentfulData: Object,
  phase3Enabled: Boolean,
  allowGaAccess: Boolean,
) => {
  const cartAppTypes = mapAppsByType(cartApps);
  const healthApp = getAppByType(cartApps, CORE_PRODUCT_TYPES.HEALTH);
  const visionApp = getAppByType(cartApps, CORE_PRODUCT_TYPES.VISION);
  const hasFFMApp =
    _.get(contentfulData, 'ffm', false) && _.get(healthApp, 'app_json.plan.qhpStatus') === true;

  let checkoutRoute;
  if (healthApp) {
    if (hasFFMApp) {
      const planYear = _.get(healthApp, 'app_json.plan.planYear');
      const planState = _.get(healthApp, 'address.state');
      checkoutRoute = phase3Enabled
        ? getEdeIntroRoute({ stateCode: planState, year: planYear, allowGaAccess })
        : '/marketplace/intro';
    } else {
      checkoutRoute = ROUTES.STATE_EXCHANGE_BUY_ROUTE;
    }
  } else {
    checkoutRoute =
      visionApp && cartAppTypes.length === 1 ? ROUTES.VISION_PURCHASE_ROUTE : ROUTES.CHECKOUT_ROUTE;
  }

  return checkoutRoute;
};

export const getCartPropsFromState = (state: CartStateType): CartPropsType => {
  const { cart, contentfulData, phase3Enabled, ifpApps: activeApps = [] } = state;
  const activeAppTypes = mapAppsByType(activeApps);
  const cartApps = _.get(cart, 'ifpApps', []);
  const cartAppTypes = mapAppsByType(cartApps);
  const dentalApp = getAppByType(cartApps, CORE_PRODUCT_TYPES.DENTAL);
  const healthApp = getAppByType(cartApps, CORE_PRODUCT_TYPES.HEALTH);
  const visionApp = getAppByType(cartApps, CORE_PRODUCT_TYPES.VISION);
  const hasFFMApp =
    _.get(contentfulData, 'ffm', false) && _.get(healthApp, 'app_json.plan.qhpStatus') === true;
  const excludeFromTotalPremiumTypes = hasFFMApp ? _.without(cartAppTypes, CORE_PRODUCT_TYPES.HEALTH) : null;
  const isHealthCareGov =
    _.get(contentfulData, 'stateExchange.name', '').toLowerCase() === HEALTH_CARE_EXCHANGE_NAME;
  const premiumTotal = getPremiumTotal(cartApps, excludeFromTotalPremiumTypes);
  const checkoutRoute = getCheckoutRoute(cartApps, contentfulData, phase3Enabled);

  return {
    activeAppTypes,
    cartApps,
    cartAppTypes,
    checkoutRoute,
    dentalApp,
    hasFFMApp,
    healthApp,
    isHealthCareGov,
    premiumTotal,
    visionApp,
  };
};
